// src/pages/customer/ProductDetail.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { getProductById, getProductReviews } from "../../services/firestoreService";
import { useCart } from "../../context/CartContext";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import {
  FiShoppingCart, FiMinus, FiPlus, FiArrowLeft, FiCheck,
  FiPackage, FiTruck, FiShield, FiStar, FiHeart, FiShare2,
} from "react-icons/fi";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import { format } from "date-fns";

const TRUST_BADGES = [
  { icon: FiTruck,   label: "Fast Delivery",    sub: "24–48 hours" },
  { icon: FiShield,  label: "Quality Assured",  sub: "100% verified" },
  { icon: FiPackage, label: "Secure Packaging", sub: "Safe & fresh" },
];

// Renders full/half/empty stars for any rating value, e.g. 4.3 -> ★★★★☆
function StarRow({ rating }) {
  const full  = Math.floor(rating);
  const half  = rating % 1 >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  return (
    <div className="flex gap-0.5">
      {[...Array(full)].map((_, i)  => <FaStar        key={`f${i}`} className="w-4 h-4 text-yellow-400" />)}
      {half === 1                    && <FaStarHalfAlt              className="w-4 h-4 text-yellow-400" />}
      {[...Array(empty)].map((_, i) => <FaRegStar      key={`e${i}`} className="w-4 h-4 text-gray-200" />)}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem, items } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx,  setImgIdx]  = useState(0);
  const [qty,     setQty]     = useState(1);
  const [wishlist, setWishlist] = useState(false);
  const [added,    setAdded]    = useState(false);
  const [reviews,  setReviews]  = useState([]);       // raw review docs for this product
  const [reviewsLoading, setReviewsLoading] = useState(true);

useEffect(() => {
  window.scrollTo({ top: 0, behavior: "instant" });
  getProductById(id).then((p) => { setProduct(p); setLoading(false); });
}, [id]);

// Fetch real reviews for this product and derive the average rating + count
// live, instead of relying on hardcoded/stale numbers on the product doc.
useEffect(() => {
  setReviewsLoading(true);
  getProductReviews(id)
    .then(setReviews)
    .catch(() => setReviews([]))
    .finally(() => setReviewsLoading(false));
}, [id]);

const reviewCount = reviews.length;
const avgRating = reviewCount > 0
  ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviewCount
  : 0;

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Spinner center /></div>;

  if (!product) return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-center px-4">
      <Helmet>
        <title>Product Not Found | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>
      <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center text-5xl mb-6">🌾</div>
      <h2 className="text-xl font-bold text-gray-700 mb-2">Product not found</h2>
      <p className="text-gray-400 mb-6">This product may have been removed or is unavailable.</p>
      <Link to="/products" className="btn-primary">Back to Products</Link>
    </div>
  );

  const inCart = items.find((i) => i.id === product.id);
  const discount = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : 0;

  const weightLabel = product.weightSize
    ? `${product.weightSize} ${product.unit || ""}`
    : null;

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice || null,
        image: product.images?.[0] || "",
        category: product.category || "",
        unit: product.unit,
        weightSize: product.weightSize || null,
        pricePerKg: product.pricePerKg || null,
        packSize: product.packSize || null,
        pricePerPack: product.pricePerPack || null,
        hsnSac: product.hsnSac || "",
      });
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
    toast.success(`${qty}× ${product.name} added to cart`);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: product.name, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied!");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      <Helmet>
        <title>{product.name ? `${product.name} | Tripura Agro` : "Tripura Agro"}</title>
        <meta
          name="description"
          content={
            product.description ||
            "Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
          }
        />
      </Helmet>

      {/* ── Breadcrumb strip ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Link
            to="/products"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            <FiArrowLeft className="w-3.5 h-3.5" /> Back to Products
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* ── Image gallery ── */}
          <div className="space-y-3">
            {/* Main image */}
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-primary-50 to-green-50 group">
              {product.images?.[imgIdx] ? (
                <img
                  src={product.images[imgIdx]}
                  alt={product.name}
                  className="w-full h-full object-contain p-4 sm:p-6 transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-8xl">🌾</div>
              )}

              {/* Discount badge */}
              {discount > 0 && (
                <div className="absolute top-4 left-4 bg-red-500 text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg">
                  {discount}% OFF
                </div>
              )}

              {/* Wishlist + Share */}
              <div className="absolute top-4 right-4 flex flex-col gap-2">
                <button
                  onClick={() => setWishlist(!wishlist)}
                  className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:scale-110 transition-transform"
                >
                  <FiHeart className={`text-lg ${wishlist ? "text-red-500 fill-red-500" : "text-gray-500"}`} />
                </button>
                <button
                  onClick={handleShare}
                  className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:scale-110 transition-transform"
                >
                  <FiShare2 className="text-lg text-gray-500" />
                </button>
              </div>

              {/* Out of stock overlay */}
              {product.inStock === false && (
                <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
                  <span className="bg-white text-gray-900 font-bold text-lg px-6 py-3 rounded-2xl shadow-lg">
                    Out of Stock
                  </span>
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={`flex-shrink-0 w-18 h-18 rounded-2xl overflow-hidden border-2 transition-all duration-200 ${
                      imgIdx === i
                        ? "border-primary-600 shadow-md scale-105"
                        : "border-gray-100 hover:border-gray-300"
                    }`}
                    style={{ width: 72, height: 72 }}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Product info ── */}
          <div className="flex flex-col gap-5">

            {/* Badges row */}
            <div className="flex flex-wrap gap-2">
              {product.category && (
                <span className="text-xs font-semibold text-primary-700 bg-primary-50 px-3 py-1 rounded-full">
                  {product.category}
                </span>
              )}
              {product.featured && (
                <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-3 py-1 rounded-full">
                  ⭐ Featured
                </span>
              )}
              {product.inStock !== false ? (
                <span className="text-xs font-semibold text-green-700 bg-green-50 px-3 py-1 rounded-full">
                  ✓ In Stock
                </span>
              ) : (
                <span className="text-xs font-semibold text-red-700 bg-red-50 px-3 py-1 rounded-full">
                  Out of Stock
                </span>
              )}
            </div>

            {/* Name */}
            <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              {product.name}
            </h1>

            {/* Rating — live, computed from actual reviews on this product */}
            <div className="flex items-center gap-2">
              {reviewsLoading ? (
                <span className="text-sm text-gray-400">Loading rating…</span>
              ) : reviewCount > 0 ? (
                <>
                  <StarRow rating={avgRating} />
                  <span className="text-sm font-medium text-gray-700">{avgRating.toFixed(1)}</span>
                  <span className="text-sm text-gray-400">({reviewCount} review{reviewCount !== 1 ? "s" : ""})</span>
                </>
              ) : (
                <span className="text-sm text-gray-400">No reviews yet</span>
              )}
            </div>

            {/* Pricing */}
            <div className="bg-gradient-to-br from-primary-50 to-green-50 rounded-2xl p-5">
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-4xl font-bold text-primary-700">₹{product.price}</span>
                {product.originalPrice && (
                  <span className="text-xl text-gray-400 line-through">₹{product.originalPrice}</span>
                )}
                {discount > 0 && (
                  <span className="text-sm font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                    Save {discount}%
                  </span>
                )}
              </div>

              {/* Weight + per kg */}
              <div className="flex flex-wrap gap-2 mt-3">
                {weightLabel && (
                  <span className="text-sm font-medium text-gray-700 bg-white px-3 py-1.5 rounded-xl border border-gray-100 shadow-sm">
                    📦 {weightLabel}
                  </span>
                )}
                {product.pricePerKg && (
                  <span className="text-sm font-medium text-primary-700 bg-white px-3 py-1.5 rounded-xl border border-primary-100 shadow-sm">
                    ₹{product.pricePerKg}/kg
                  </span>
                )}
                {!weightLabel && product.unit && (
                  <span className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded-xl border border-gray-100">
                    per {product.unit}
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            {product.description && (
              <p className="text-gray-600 leading-relaxed text-sm">{product.description}</p>
            )}

            {/* Qty + Add to cart */}
            {product.inStock !== false && (
              <div className="flex items-center gap-3">
                {/* Qty stepper */}
                <div className="flex items-center bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="px-4 py-3 hover:bg-gray-50 text-gray-600 transition-colors"
                  >
                    <FiMinus className="w-4 h-4" />
                  </button>
                  <span className="px-5 py-3 font-bold text-gray-900 min-w-[3rem] text-center text-lg">
                    {qty}
                  </span>
                  <button
                    onClick={() => setQty(qty + 1)}
                    className="px-4 py-3 hover:bg-gray-50 text-gray-600 transition-colors"
                  >
                    <FiPlus className="w-4 h-4" />
                  </button>
                </div>

                {/* Add button */}
                <button
                  onClick={handleAdd}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl font-semibold text-base transition-all duration-200 shadow-sm
                    ${added
                      ? "bg-green-600 text-white scale-[0.98]"
                      : "bg-primary-600 hover:bg-primary-700 text-white hover:shadow-md"
                    }`}
                >
                  {added
                    ? <><FiCheck className="w-5 h-5" /> Added!</>
                    : <><FiShoppingCart className="w-5 h-5" /> {inCart ? "Add More" : "Add to Cart"}</>
                  }
                </button>
              </div>
            )}

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-3">
              {TRUST_BADGES.map(({ icon: Icon, label, sub }) => (
                <div key={label} className="bg-white border border-gray-100 rounded-2xl p-3 text-center">
                  <div className="w-9 h-9 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <Icon className="w-4 h-4 text-primary-600" />
                  </div>
                  <p className="text-xs font-semibold text-gray-800 leading-snug">{label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
                </div>
              ))}
            </div>

            {/* Product specs */}
            {product.specs && Object.keys(product.specs).length > 0 && (
              <div className="bg-white border border-gray-100 rounded-2xl p-5">
                <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide">
                  Product Details
                </h3>
                <div className="space-y-3">
                  {Object.entries(product.specs).map(([k, v]) => (
                    <div key={k} className="flex items-center text-sm border-b border-gray-50 pb-3 last:border-0 last:pb-0">
                      <span className="w-36 text-gray-400 capitalize font-medium">{k}</span>
                      <span className="text-gray-800 font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}